import { API_CALL } from 'actions/actionConstants'
import type { AppAction } from 'actions/types/ActionTypes'
import { ENROL_LOYALTY_ACCOUNT } from 'actions/apiActionConstants'
import { trackClientEvent } from 'analytics/analytics'
import { loyaltyContext } from 'analytics/snowplow/eventContexts/loyaltyContext'
import requestLoyaltyAccountEnrolment from 'api/loyalty/requestLoyaltyAccountEnrolment'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import config from 'constants/config'
import { appViewMessageHandler } from 'lib/window/webViewUtils'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import isValidLoyaltyRegion from 'api/lib/isValidLoyaltyRegion'
import { getCurrentIntl } from 'i18n/util/currentIntl'

interface EnrolLoyaltyAccount {
  accountMemberId?: string
  token?: string
  enableSnackbar?: boolean
  enrolment?: App.LoyaltyEnrolmentType | undefined
}

/**
 * Enrols a user in the loyalty program
 *
 * @param {EnrolLoyaltyAccount} params - The enrollment parameters
 * @param {string} [params.accountMemberId] - Optional member ID to enrol. If not provided, uses the current authenticated user's member ID
 * @param {string} [params.token] - Optional authentication token for the enrollment API request
 * @param {boolean} [params.enableSnackbar] - Whether to show snackbar notifications (currently unused in implementation)
 * @param {App.LoyaltyEnrolmentType} [params.enrolment] - The type of enrollment (e.g., 'newUserRegistration', 'existingUserRegistration')
 *
 * @returns {AppAction} A Redux thunk action that dispatches the enrollment API call
 *
 * @description
 * This function handles the enrollment of users into the loyalty program. It:
 * - Validates that loyalty is enabled and the user doesn't already have an account
 * - Checks if the user's region is valid for loyalty enrollment
 * - Makes an API call to enrol the user with their member ID and region
 * - Shows appropriate success messages based on enrollment type
 * - Tracks analytics events for the enrollment
 * - Sends messages to mobile WebView if in headless mode
 *
 * The function will exit early without taking action if:
 * - Loyalty is not enabled for the current state
 * - No member ID is available
 * - User already has a loyalty account
 * - A loyalty account fetch is in progress or has errored
 * - The user's region is not valid for loyalty
 *
 * @example
 * // Enrol current authenticated user
 * dispatch(enrolLoyaltyAccount({ enrolment: 'newUserRegistration' }))
 */
function enrolLoyaltyAccount({ accountMemberId, token, enrolment }: EnrolLoyaltyAccount): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const memberId = accountMemberId ?? state.auth.account.memberId
    const givenName = state.auth.account.givenName
    const headlessMode = state.system.headlessMode
    const hasLoyaltyAccount = getHasLoyaltyAccount(state)
    const loyaltyAccount = state.loyalty.account
    const regionCode = state.geo.currentRegionCode

    let enrolled = false

    if (
      !getIsLoyaltyEnabled(state) ||
      !memberId ||
      hasLoyaltyAccount ||
      loyaltyAccount.fetching ||
      loyaltyAccount.error ||
      !isValidLoyaltyRegion(regionCode)
    ) return

    dispatch({
      type: API_CALL,
      api: ENROL_LOYALTY_ACCOUNT,
      enrolment,
      request: () => requestLoyaltyAccountEnrolment({
        customerId: memberId,
        region: regionCode,
      }, token)
        .then((result) => {
          const intl = getCurrentIntl()
          enrolled = true
          let analyticsAction = 'default'

          if (enrolment === 'newUserRegistration') {
            analyticsAction = 'sign_up'
            const loyaltyName = getLoyaltyProgramDisplayName()
            const welcomeMessage = givenName
              ? intl.formatMessage({
                  id: 'Ac56kCMxlGfjDbb-yrTx',
                  defaultMessage: 'Welcome to {title} and your new {loyaltyName} membership, {givenName}. Start earning today.',
                }, { title: config.title, loyaltyName, givenName })
              : intl.formatMessage({
                  id: 'Ec2GlHS2By8tlJJAQjQp',
                  defaultMessage: 'Welcome to {title} and your new {loyaltyName} membership. Start earning today.',
                }, { title: config.title, loyaltyName })
            showSnackbar(welcomeMessage, 'success')
          }

          if (enrolment === 'existingUserRegistration') {
            analyticsAction = 'opt_in'
            const loyaltyName = getLoyaltyProgramDisplayName()
            const welcomeMessage = givenName
              ? intl.formatMessage({
                  id: 'Fyf6rRcwSezG-0OtfAX8',
                  defaultMessage: 'Welcome to your new {loyaltyName} membership, {givenName}. Start earning today.',
                }, { loyaltyName, givenName })
              : intl.formatMessage({
                  id: 'KedLJn3Nx9krLQfhxhh5',
                  defaultMessage: 'Welcome to your new {loyaltyName} membership. Start earning today.',
                }, { loyaltyName })
            showSnackbar(welcomeMessage, 'success')
          }

          if (result?.account?.accountNumber) {
            trackClientEvent({
              action: analyticsAction,
              category: 'loyalty_sign_up',
              subject: result.account.accountNumber + '_' + result.account.tier,
              type: 'operational',
              options: {
                contextsToSkip: [
                  'loyalty',
                ],
              },
              context: [
                loyaltyContext(result.account),
              ],
            },
            )
          }
          return result
        })
        .catch((err) => {
          const intl = getCurrentIntl()
          showSnackbar(
            intl.formatMessage({
              id: 'OJOLxB1QiRFl-CPrRGD_',
              defaultMessage: 'Please try again later or contact us for assistance.',
            }),
            'critical',
            {
              heading: intl.formatMessage({
                id: 'TMIRgOTmHYXSa0nc6Ode',
                defaultMessage: 'Oops! Something went wrong',
              }),
            },
          )

          throw err
        })
        .finally(() => {
          // Send message to mobile WebView
          if (headlessMode) {
            appViewMessageHandler('luxLoyalty', { enrolled })
          }
        }),
    })
  }
}

export default enrolLoyaltyAccount
